import { supabase } from './supabase'
import { contactAffiche } from './commun'
import { MESSAGE_COMPTE_EXISTANT, adresseDejaInscrite, traduireErreurInscription } from './compte'
import { controlerAcces } from '../../../../../web-commun/connexion-google'
import { sessionOuverteParGoogle } from './google'
import { traduireErreurConnexion } from './oauth'

export async function connecterParEmail(email: string, motDePasse: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password: motDePasse })
  if (error) throw new Error(`Connexion refusée : ${traduireErreurConnexion(error.message)}`)
}

/**
 * Nom affiché pour le compte connecté : l'e-mail, sinon le numéro formaté (compte connecté par code WhatsApp : pas
 * d'e-mail), sinon « — ». Supabase range le numéro SANS « + » dans la session.
 */
export function libelleCompte(user: { email?: string | null | undefined; phone?: string | null | undefined } | null | undefined): string {
  const chiffres = (user?.phone ?? '').replace(/\D+/g, '')
  return contactAffiche(user?.email, chiffres ? `+${chiffres}` : null) || '—'
}

export type ResultatAcces = { ok: true; id: string; email: string } | { ok: false; message: string }

export const MESSAGE_PAS_ADMIN = "Ce compte n'a pas accès à la supervision de la plateforme."

/**
 * Contrôle d'accès commun à la connexion e-mail, à Google, au code WhatsApp et à la restauration de session :
 * `is_platform_admin()` côté serveur fait foi. Un compte qui n'est pas administrateur de la plateforme est déconnecté
 * sur-le-champ — c'est aussi le cas d'un compte Google quelconque, que le serveur a pu créer au passage : il ne voit
 * RIEN, et n'apprend rien (une seule phrase pour tous les refus d'une session Google).
 */
export async function verifierAcces(): Promise<ResultatAcces> {
  const sortir = async () => {
    await supabase.auth.signOut()
  }
  try {
    const v = await controlerAcces({
      viaGoogle: await sessionOuverteParGoogle(),
      deconnecter: sortir,
      evaluer: async () => {
        const { data: admin, error } = await supabase.rpc('is_platform_admin')
        if (error) throw new Error(`Vérification impossible : ${error.message}`)
        if (admin !== true) return { ok: false, message: MESSAGE_PAS_ADMIN }
        const { data } = await supabase.auth.getUser()
        return { ok: true, acces: { id: data.user?.id ?? '', email: libelleCompte(data.user) } }
      },
    })
    return v.ok ? { ok: true, ...v.acces } : v
  } catch (e) {
    /* Hors Google, une erreur de vérification remonte ici : déconnexion et message, comme avant. */
    await sortir().catch(() => undefined)
    return { ok: false, message: e instanceof Error ? e.message : 'Vérification impossible.' }
  }
}

export async function deconnecter(): Promise<void> {
  try {
    await supabase.auth.signOut()
  } catch {
    /* La session locale est de toute façon abandonnée par l'appelant. */
  }
}

/**
 * Crée un compte e-mail + mot de passe. N'écrit AUCUNE ligne (rôle, profil,
 * équipe) : les droits sont accordés ensuite par un administrateur.
 * `sessionOuverte` = faux quand la confirmation par e-mail est active.
 */
export async function creerCompte(
  nom: string,
  email: string,
  motDePasse: string,
): Promise<{ sessionOuverte: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password: motDePasse,
    options: {
      data: { full_name: nom },
      emailRedirectTo: window.location.origin + window.location.pathname,
    },
  })
  if (error) throw new Error(traduireErreurInscription(error.message, error.code, error.status))
  if (adresseDejaInscrite(data.user)) throw new Error(MESSAGE_COMPTE_EXISTANT)
  return { sessionOuverte: !!data.session }
}
